import _ from 'lodash';
import { parse } from 'json2csv';
import { isUndefinedOrNullOrEmpty } from '@/utils/inspect';

export const isObject = obj => obj !== null && typeof obj === 'object';

export const defineProperties = (obj, props) =>
	Object.defineProperties(obj, props);

export const defineProperty = (obj, prop, descriptor) =>
	Object.defineProperty(obj, prop, descriptor);

export const keys = obj => Object.keys(obj);

export const hasOwnProperty = (obj, prop) =>
	Object.prototype.hasOwnProperty.call(obj, prop);

export const isEmpty = obj => {
	if (!isObject(obj)) {
		return true;
	}
	for (const prop in obj) {
		if (hasOwnProperty(prop)) {
			return false;
		}
	}
	return JSON.stringify(obj) === JSON.stringify({});
};

export const clone = obj => ({ ...obj });

/**
 * Return true if the objects are equal
 * @param {*} obj1
 * @param {*} obj2
 * @returns boolean
 */
export const deepEqual = (obj1, obj2, objMustCleaned, ignoreProps) => {
	if (isEmpty(obj1) || isEmpty(obj2)) {
		return JSON.stringify(obj1) === JSON.stringify(obj2);
	}
	let cloneObj1 = Object.assign({}, obj1);
	let cloneObj2 = Object.assign({}, obj2);

	if (objMustCleaned !== false) {
		objMustCleaned = true;
	}
	//clean properties null or empty
	const cleanObj = obj => {
		for (var propName in obj) {
			if (
				(ignoreProps && ignoreProps.includes(propName)) ||
				obj[propName] === null ||
				obj[propName] === undefined ||
				obj[propName] === ''
			) {
				delete obj[propName];
			}
		}
		return obj;
	};

	if (objMustCleaned) {
		cloneObj1 = cleanObj(cloneObj1);
		cloneObj2 = cleanObj(cloneObj2);
	}

	let equals = _.isEqual(cloneObj1, cloneObj2);
	return equals;
};

export const compare = (obj1, obj2) => {
	return deepEqual(obj1, obj2);
};

// --- Utilities ---
export const sortAndStringify = (obj, sortable) => {
	const ordered = {};

	obj.keys(sortable)
		.sort()
		.forEach(key => {
			ordered[key] = sortable[key];
		});

	return JSON.stringify(ordered);
};

export const merge = (obj = {}, defaults) => {
	for (const key in defaults) {
		if (typeof obj[key] === 'undefined') {
			obj[key] = defaults[key];
		}
	}
	return obj;
};

export const parseObj = (source, data) => {
	if (isObject(data)) {
		var keys = keys(data);
		keys.forEach(k => {
			if (!data[k]) {
				if (source[k]) {
					delete source[k];
				}
			}
			source[k] = data[k];
		});
	}
};

// compare to objects and return the difference with the format
export const compareObjAndReturnDiff = (
	initialObjValue,
	newObjValue,
	removeFn = true
) => {
	const comp = _.isEqual(initialObjValue, newObjValue);
	if (comp) {
		return {};
	}

	// remove all the functions
	if (removeFn) {
		for (let prop in initialObjValue) {
			if (typeof initialObjValue[prop] === 'function') {
				delete initialObjValue[prop];
			}
		}
		for (let prop in newObjValue) {
			if (typeof newObjValue[prop] === 'function') {
				delete newObjValue[prop];
			}
		}
	}

	const result = _.transform(initialObjValue, (result, value, property) => {
		if (
			_.has(newObjValue, property) &&
			!_.isEqual(value, newObjValue[property])
		) {
			if (
				_.isObject(value) &&
				_.isObject(newObjValue[property]) &&
				_.isEqual(value, newObjValue[property])
			) {
				return;
			}
			result[property] = {
				initialValue: value,
				newValue: newObjValue[property]
			};
		}
	});
	return Object.entries(result);
};

export const compareArrayAndReturnDiff = (arr1, arr2) => {
	const result = [];

	arr1.forEach(item1 => {
		if (
			!arr2.some(item2 => JSON.stringify(item1) === JSON.stringify(item2))
		) {
			result.push(item1);
		}
	});
	arr2.forEach(item2 => {
		if (
			!arr1.some(item1 => JSON.stringify(item2) === JSON.stringify(item1))
		) {
			result.push(item2);
		}
	});
	return result;
};

export const removeDuplicatesFromArray = originalArray => {
	var uniqueArray = Array.from(
		new Set(originalArray.map(obj => JSON.stringify(obj)))
	);
	return uniqueArray.map(str => JSON.parse(str));
};

export const calculatePageIndices = (array, page, pageSize) => {
	const totalItems = array.length;
	const totalPages = Math.ceil(totalItems / pageSize);
	const startIndex = (page - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	const pageArray = array.slice(startIndex, endIndex);
	return {
		start: startIndex,
		end: endIndex,
		items: pageArray,
		totalPages: totalPages
	};
};

export const updatePagination = (page, pageSize, itemsArray) => {
	const { items, totalPages } = calculatePageIndices(
		itemsArray,
		page,
		pageSize
	);
	return {
		countPerPage: pageSize,
		items,
		page,
		totalCount: itemsArray.length,
		totalPageCount: totalPages
	};
};

export const deepEqualNoFn = (
	obj1,
	obj2,
	considerNullAndUndefinedEqual = false
) => {
	if (obj1 === obj2) {
		return true;
	}

	if (considerNullAndUndefinedEqual && obj1 == null && obj2 == null) {
		return true;
	}

	if (
		typeof obj1 !== 'object' ||
		typeof obj2 !== 'object' ||
		obj1 === null ||
		obj2 === null
	) {
		return false;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (let key of keys1) {
		if (
			!Object.prototype.hasOwnProperty.call(obj2, key) ||
			!deepEqualNoFn(obj1[key], obj2[key], considerNullAndUndefinedEqual)
		) {
			return false;
		}
	}

	return true;
};

/**
 * Return true if the objects are equal
 * @param {*} obj
 * @returns cloneObj
 */
export const deleteEmptyKeys = obj => {
	let cloneObj = _.cloneDeep(obj);
	_.forEach(cloneObj, (value, key) => {
		if (
			isUndefinedOrNullOrEmpty(value) ||
			(_.isArray(value) && _.isEmpty(value)) ||
			(isObject(value) && _.isEmpty(value))
		) {
			_.unset(cloneObj, key);
		}
	});
	return cloneObj;
};

/**
 * Return a new payload Object
 * @param {*} payload
 * @param {*} newRequest
 * @returns cloneObj
 */
export const mergePayloadObjects = (currentStore, newRequest) => {
	// Extract items from the currentStore
	const { items: oldItems } = currentStore;
	// Extract items from the new request
	const { items: newItems } = newRequest;

	// Merge old and new items
	const mergedItems = [...oldItems, ...newItems];
	// remove duplicates
	const uniqueStringObjects = new Set(
		mergedItems.map(obj => JSON.stringify(obj))
	);

	// Convert string representations back to objects
	const uniqueObjects = Array.from(uniqueStringObjects).map(str =>
		JSON.parse(str)
	);
	const page = newRequest.page;
	const countPerPage = uniqueObjects.length;

	// Create a new currentStore with merged items
	const mergedPayload = {
		...currentStore,
		page: page,
		items: uniqueObjects,
		countPerPage: countPerPage
	};

	return mergedPayload;
};

export const removeObjectEmptyValues = obj => {
	return _.pickBy(obj, value => {
		return !_.isNil(value) && !_.isEqual(value, '');
	});
};

export const removeObjectOrNestedObjectEmptyValues = obj => {
	return _.pickBy(obj, isNullOrEmpty);
};

let isNullOrEmpty = value => {
	if (!_.isObject(value)) {
		return !_.isNil(value) && !_.isEqual(value, '');
	} else {
		let newValue = _.pickBy(value, val => {
			return !_.isNil(val) && !_.isEqual(val, '');
		});
		if (!_.isEmpty(newValue)) return newValue;
	}
};

export const removePropertiesWithSameValues = (obj1, obj2) => {
	const newObj = {};

	for (const key in obj1) {
		if (!(key in obj2) || !_.isEqual(obj1[key], obj2[key])) {
			newObj[key] = obj1[key];
		}
	}

	return newObj;
};

export const filterObjectByInitial = (obj, initialObj) => {
	const filteredEntries = Object.entries(obj).filter(
		([key, value]) =>
			Object.prototype.hasOwnProperty.call(initialObj, key) &&
			initialObj[key] === value
	);
	return Object.fromEntries(filteredEntries);
};

export const downloadCSV = (jsonData, csvOptions, fileName) => {
	/* 	// jsonData format example
	const jsonData = [
		{ name: 'Alice', age: 25, city: 'New York' },
		{ name: 'Bob', age: 30, city: 'San Francisco' }
		// add as needed
	];

	// csvOptions format example
	const csvOptions = {
		fields: ['name', 'age', 'city'], // fields to include
		delimiter: ',', // delimiter CSV (coma is defecto)
		withBOM: true // BOM on true allow to add special characters
	}; 
	
	// fileName
	filename will be use to export (not include extension, extension is always csv)
	*/

	try {
		const csv = parse(jsonData, csvOptions);
		downloadFile(csv, 'text/csv', fileName + '.csv');
	} catch (err) {
		console.error(err);
	}
};

export const downloadFile = (data, type, fileName) => {
	try {
		const blob = new Blob([data], { type });

		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `${fileName}`);

		link.click();

		URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Error creating CSV file:', error);
	}
};

export const createTextValueObj = (text, stringToSearch) => {
	if (!stringToSearch || !text) {
		return text;
	}

	const stringLC = text.toLowerCase();
	const searchValueLC = stringToSearch.toLowerCase();

	if (stringLC.includes(searchValueLC)) {
		const firstIndex = stringLC.indexOf(searchValueLC);

		const matchedString = text.slice(
			firstIndex,
			firstIndex + stringToSearch.length
		);

		const notMatchStartString = text.slice(0, firstIndex);
		const notMatchEndString = text.slice(
			firstIndex + stringToSearch.length,
			text.length
		);

		return `${notMatchStartString}<mark>${matchedString}</mark>${notMatchEndString}`;
	}
	return text;
};
